import type { IPathAnalysisData, IPathAnalysisFirstLevel } from '~/modules/path-analysis';
import type { IAnalyticLineChartData } from '~/modules/campaign-analytics';
import type { ITemplateData } from '~/modules/campaign-template';
import type { Campaign } from '~/modules/campaign/types';

import { THUMB_SHOPIFY, THUMB_VARIANT_MOCK } from '~/constants/thumbnail';
import { generateIntervalMetrics } from '../utils/mock-data';
import { CAMPAIGN_PRIORITY } from '~/constants/campaign';
import { IVariantType } from '~/modules/campaign/types';
import { TemplateType } from '~/types/template';

const CAMPAIGN_VARIANT_A_ID = 'campaign-variant-a-id';
const CAMPAIGN_VARIANT_B_ID = 'campaign-variant-b-id';

const VARIANT_A_BASE_METRICS = {
  sessions: 150,
  visitors: 120,
  orders: 18,
  revenue: 850,
  pageViews: 280,
  addedToCart: 12,
  timeOnPage: 49500,
  clickedHasHref: 35,
  clicked: 50,
  direct: 80,
  organic: 45,
  social: 25,
  newVisitors: 80,
  returningVisitors: 40,
  desktop: 90,
  mobile: 45,
  tablet: 15,
  pageViewedWithTime: 200,
  reachedCheckout: 100,
  completedCheckout: 50,
  reachedAndCompleted: 30,
  withoutAction: 5,
  withCartAdditions: 100,
  continuationSession: 100,
};

const VARIANT_B_BASE_METRICS = {
  sessions: 200,
  visitors: 130,
  orders: 20,
  revenue: 950,
  pageViews: 300,
  addedToCart: 13,
  timeOnPage: 54400,
  clickedHasHref: 37,
  clicked: 53,
  direct: 85,
  organic: 48,
  social: 27,
  newVisitors: 85,
  returningVisitors: 45,
  desktop: 96,
  mobile: 48,
  tablet: 16,
  pageViewedWithTime: 220,
  reachedCheckout: 110,
  completedCheckout: 55,
  reachedAndCompleted: 35,
  withoutAction: 12,
  withCartAdditions: 110,
  continuationSession: 100,
};

const VARIANT_A_PREVIOUS_METRICS = {
  sessions: 165,
  visitors: 108,
  orders: 20,
  revenue: 765,
  pageViews: 308,
  addedToCart: 11,
  timeOnPage: 54450,
  clickedHasHref: 32,
  clicked: 55,
  direct: 88,
  organic: 41,
  social: 27,
  newVisitors: 88,
  returningVisitors: 36,
  desktop: 99,
  mobile: 41,
  tablet: 16,
  pageViewedWithTime: 180,
  reachedCheckout: 110,
  completedCheckout: 45,
  reachedAndCompleted: 33,
  withoutAction: 8,
  withCartAdditions: 90,
  continuationSession: 100,
};

const VARIANT_B_PREVIOUS_METRICS = {
  sessions: 180,
  visitors: 143,
  orders: 18,
  revenue: 1045,
  pageViews: 270,
  addedToCart: 14,
  timeOnPage: 48960,
  clickedHasHref: 41,
  clicked: 48,
  direct: 77,
  organic: 53,
  social: 24,
  newVisitors: 77,
  returningVisitors: 50,
  desktop: 86,
  mobile: 53,
  tablet: 14,
  pageViewedWithTime: 242,
  reachedCheckout: 99,
  completedCheckout: 61,
  reachedAndCompleted: 32,
  withoutAction: 22,
  withCartAdditions: 121,
  continuationSession: 100,
};

export const TEMPLATE_DATA: ITemplateData = {
  variantA: {
    label: 'Default Page Template',
    thumbnail: THUMB_SHOPIFY,
    isWinner: false,
    templateInfo: {
      type: TemplateType.PAGE,
      suffix: 'landing-page',
    },
  },
  variantB: {
    label: 'Landing Page for Black Friday',
    thumbnail: THUMB_VARIANT_MOCK,
    isWinner: false,
    templateInfo: {
      type: TemplateType.PAGE,
      suffix: 'black-friday-landing-page',
    },
  },
};

export const METRIC_INTERVAL_DATA: IAnalyticLineChartData['metricInterval'] =
  generateIntervalMetrics({
    variantABaseMetrics: VARIANT_A_BASE_METRICS,
    variantBBaseMetrics: VARIANT_B_BASE_METRICS,
  });

export const CAMPAIGN_DATA: Campaign = {
  id: CAMPAIGN_VARIANT_A_ID,
  name: 'Product Template A/B Test',
  type: 'TEMPLATE',
  status: 'RUNNING',
  priority: CAMPAIGN_PRIORITY.CONVERSION,
  storeDomain: 'demo-store.myshopify.com',
  targetDevices: ['all'],
  targetChannels: ['all'],
  targetCountries: ['all'],
  targetVisitorTypes: ['all'],
  conditions: {
    targetPageIds: ['page-1', 'page-2'],
    targetMarkets: ['US', 'VN'],
  },
  createdAt: '2024-01-15T10:00:00Z',
  updatedAt: '2024-01-20T15:30:00Z',
  startedAt: '2024-01-16T09:00:00Z',
  deletedAt: '',
  completedAt: '',
  group: {
    id: 'group-456',
    title: 'Product Template Campaign Group',
    type: 'TEMPLATE',
  },
  versions: [
    {
      id: 'version-1',
      version: 'v1.0',
      startedAt: '2024-01-16T09:00:00Z',
      completedAt: '',
    },
  ],
  variants: [
    {
      id: CAMPAIGN_VARIANT_A_ID,
      isOrigin: true,
      isWinner: false,
      trafficRate: 50,
      themePageID: 'default-product-page',
      templateType: 'PRODUCT',
      templateSuffix: 'default-product-template',
      createdAt: '2024-01-15T10:00:00Z',
      deletedAt: '',
      updatedAt: '2024-01-20T15:30:00Z',
      template: {
        id: 'template-a-123',
        redirectURL: 'https://demo-store.myshopify.com/products/default-template',
        themeID: 'default-theme-123',
        createdAt: '2024-01-15T10:00:00Z',
        deletedAt: '',
        updatedAt: '2024-01-20T15:30:00Z',
      },
    },
    {
      id: CAMPAIGN_VARIANT_B_ID,
      isOrigin: false,
      isWinner: false,
      trafficRate: 50,
      themePageID: 'high-season-product-page',
      templateType: 'PRODUCT',
      templateSuffix: 'high-season-product-template',
      createdAt: '2024-01-15T10:00:00Z',
      deletedAt: '',
      updatedAt: '2024-01-20T15:30:00Z',
      template: {
        id: 'template-b-456',
        redirectURL: 'https://demo-store.myshopify.com/products/high-season-template',
        themeID: 'high-season-theme-456',
        createdAt: '2024-01-15T10:00:00Z',
        deletedAt: '',
        updatedAt: '2024-01-20T15:30:00Z',
      },
    },
  ],
  campaignVersionId: 'version-1',
  campaignGroupVersionId: 'group-version-1',
};

// Helper function to generate metrics data from base metrics
const generateMetricsFromBase = (
  baseMetrics: typeof VARIANT_A_BASE_METRICS,
  variantKey: IVariantType,
  date: string,
) => {
  return {
    date,
    totalOrders: baseMetrics.orders,
    totalRevenue: baseMetrics.revenue,
    totalVisitors: baseMetrics.visitors,
    totalPageViewed: baseMetrics.pageViews,
    totalAddedToCart: baseMetrics.addedToCart,
    totalSessions: baseMetrics.sessions,
    totalTimeOnPage: baseMetrics.timeOnPage,
    totalClickedHasHref: baseMetrics.clickedHasHref,
    totalClicked: baseMetrics.clicked,
    visitors: [
      { totalCount: baseMetrics.newVisitors, type: 'new' },
      { totalCount: baseMetrics.returningVisitors, type: 'returning' },
    ],
    devices: [
      { totalCount: baseMetrics.desktop, type: 'desktop' },
      { totalCount: baseMetrics.mobile, type: 'mobile' },
      { totalCount: baseMetrics.tablet, type: 'tablet' },
    ],
    channels: [
      { totalCount: baseMetrics.direct, type: 'direct' },
      { totalCount: Math.round(baseMetrics.organic * 0.6), type: 'organic-search' },
      { totalCount: Math.round(baseMetrics.organic * 0.4), type: 'organic-social' },
      { totalCount: Math.round(baseMetrics.social * 0.7), type: 'paid-social' },
      { totalCount: Math.round(baseMetrics.social * 0.3), type: 'social' },
      { totalCount: Math.round(baseMetrics.direct * 0.2), type: 'email' },
      { totalCount: Math.round(baseMetrics.direct * 0.15), type: 'referral' },
      { totalCount: Math.round(baseMetrics.direct * 0.1), type: 'sms' },
    ],
    variantKey,
    totalPageViewedHaveTimeOnPage: baseMetrics.pageViewedWithTime,
    totalReachedCheckout: baseMetrics.reachedCheckout,
    totalSessionsThatCompletedCheckout: baseMetrics.completedCheckout,
    totalSessionsThatReachedAndCompletedCheckout: baseMetrics.reachedAndCompleted,
    totalSessionsThatReachedCheckout: baseMetrics.reachedCheckout,
    totalSessionsWithoutAction: baseMetrics.withoutAction,
    totalSessionsWithCartAdditions: baseMetrics.withCartAdditions,
    totalContinuationSession: baseMetrics.continuationSession,
  };
};

export const METRICS_DATA = [
  generateMetricsFromBase(VARIANT_A_BASE_METRICS, IVariantType.VARIANT_A, '2025-01-01'),
  generateMetricsFromBase(VARIANT_B_BASE_METRICS, IVariantType.VARIANT_B, '2025-01-01'),
];

export const METRICS_DATA_PREVIOUS = [
  generateMetricsFromBase(VARIANT_A_PREVIOUS_METRICS, IVariantType.VARIANT_A, '2025-01-01'),
  generateMetricsFromBase(VARIANT_B_PREVIOUS_METRICS, IVariantType.VARIANT_B, '2025-01-01'),
];

//journey analysis
export const VARIANT_A_PATH_ANALYSIS_DATA: IPathAnalysisData = {
  pathAnalytics: [
    {
      id: 'landing-page',
      level: 1,
      locationPath: '/',
      parentID: '',
      pageType: TemplateType.PAGE,
      shopifyPageID: 'home-1',
      pageTitle: 'Default Page Template',
      metric: METRICS_DATA[0],
    },
    {
      id: 'product-page',
      level: 2,
      locationPath: '/products',
      parentID: 'landing-page',
      pageType: TemplateType.PRODUCT,
      shopifyPageID: 'product-1',
      pageTitle: 'Product page',
      metric: {
        totalSessions: 100,
        totalPageViewed: 200,
        totalVisitors: 100,
        totalClicked: 100,
        totalAddedToCart: 80,
        totalReachedCheckout: 20,
        totalOrders: 50,
        totalSessionsThatCompletedCheckout: 20,
        totalSessionsThatReachedCheckout: 50,
        totalSessionsWithCartAdditions: 80,
        totalClickedHasHref: 20,
        totalSessionsWithoutAction: 100,
        totalContinuationSession: 100,
      },
    },
  ],
  previousPathAnalyticsMap: {
    'landing-page': {
      id: 'landing-page',
      level: 1,
      locationPath: '/',
      parentID: '',
      pageType: TemplateType.PAGE,
      shopifyPageID: 'landing-1',
      pageTitle: 'Landing Page for Black Friday',
      metric: METRICS_DATA_PREVIOUS[0],
    },
    'product-page': {
      id: 'product-page',
      level: 2,
      locationPath: '/products',
      parentID: 'landing-page',
      pageType: TemplateType.PRODUCT,
      shopifyPageID: 'product-1',
      pageTitle: 'Product page',
      metric: {
        totalSessions: 5600, // 25% decrease from 7000
        totalPageViewed: 5600,
        totalVisitors: 5600,
        totalClicked: 3840,
        totalAddedToCart: 3840,
        totalReachedCheckout: 2320,
        totalOrders: 880,
        totalSessionsThatCompletedCheckout: 880,
        totalSessionsThatReachedCheckout: 2320,
        totalSessionsWithCartAdditions: 3840,
        totalClickedHasHref: 100,
        totalSessionsWithoutAction: 100,
        totalContinuationSession: 100,
      },
    },
  },
};

export const VARIANT_B_PATH_ANALYSIS_DATA: IPathAnalysisData = {
  pathAnalytics: [
    {
      id: 'landing-page',
      level: 1,
      locationPath: '/',
      parentID: '',
      pageType: TemplateType.PAGE,
      shopifyPageID: 'home-1',
      pageTitle: 'Default Page Template',
      metric: METRICS_DATA[1],
    },
    {
      id: 'product-page',
      level: 2,
      locationPath: '/products',
      parentID: 'landing-page',
      pageType: TemplateType.PRODUCT,
      shopifyPageID: 'product-1',
      pageTitle: 'Product page',
      metric: {
        totalSessions: 150,
        totalPageViewed: 200,
        totalVisitors: 100,
        totalClicked: 100,
        totalAddedToCart: 100,
        totalReachedCheckout: 70,
        totalOrders: 50,
        totalSessionsThatCompletedCheckout: 10,
        totalSessionsThatReachedCheckout: 50,
        totalSessionsWithCartAdditions: 80,
        totalClickedHasHref: 20,
        totalSessionsWithoutAction: 100,
        totalContinuationSession: 100,
      },
    },
  ],
  previousPathAnalyticsMap: {
    'landing-page': {
      id: 'landing-page',
      level: 1,
      locationPath: '/',
      parentID: '',
      pageType: TemplateType.PAGE,
      shopifyPageID: 'landing-1',
      pageTitle: 'Landing Page for Black Friday',
      metric: METRICS_DATA_PREVIOUS[1],
    },
    'product-page': {
      id: 'product-page',
      level: 2,
      locationPath: '/products',
      parentID: 'landing-page',
      pageType: TemplateType.PRODUCT,
      shopifyPageID: 'product-1',
      pageTitle: 'Product page',
      metric: {
        totalSessions: 50,
        totalPageViewed: 50,
        totalVisitors: 50,
        totalClicked: 50,
        totalAddedToCart: 40,
        totalReachedCheckout: 60,
        totalOrders: 50,
        totalSessionsThatCompletedCheckout: 50,
        totalSessionsThatReachedCheckout: 50,
        totalSessionsWithCartAdditions: 40,
        totalClickedHasHref: 100,
        totalSessionsWithoutAction: 100,
        totalContinuationSession: 100,
      },
    },
  },
};

export const LIST_PAGE_ASSIGNED_BY_VARIANT: IPathAnalysisFirstLevel[] = [
  {
    id: 'landing-page',
    locationPath: '/',
    pageTitle: 'Landing Page for Black Friday',
    pageType: TemplateType.PAGE,
    parentID: '',
    level: 1,
    metric: {
      totalSessions: 100,
    },
  },
];
